import { Box } from "@mui/material";
import { type FC, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../components/InHouseTooltip/InHouseTooltip";
import { DashedAddButton } from "../../components/UIToolkit/Clickable/DashedAddButton";
import { InfoIconWithTooltip } from "../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { InlineHint } from "../../components/UIToolkit/molecules/InlineHint";
import { CollapsibleSection, CollapsibleSectionVariant } from "../../components/UIToolkit/Panels/CollapsibleSection";
import SystemDialog from "../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { ICON_SIZE, INK } from "../../theme/tokens";
import { PrintoutPresetEditor } from "./PrintoutPresetEditor";
import { PrintoutPresetPicker } from "./PrintoutPresetPicker";
import { PRINTOUT_LIST_CARD_SX } from "./printoutPresetRowStyles";
import { MAX_PRINTOUT_PRESETS, type PrintoutPreset } from "./printoutPresets";

type Props = {
	status: "loading" | "error" | "ready";
	/** Saved printouts only (Printout 2+). */
	presets: PrintoutPreset[];
	/** `null` = Printout 1. */
	activePresetId: string | null;
	isAtLimit: boolean;
	isAdding: boolean;
	/** A retry of a failed load is on its way. */
	isRetrying: boolean;
	/** Annie is changing the printout on screen: switching, adding and removing wait for her answer. */
	locked: boolean;
	onSelect: (presetId: string | null) => void;
	onAdd: () => void;
	/** Resolves whether the server took the name. */
	onRename: (presetId: string, name: string) => Promise<boolean>;
	/** Resolves whether the printout was removed. */
	onRemove: (presetId: string) => Promise<boolean>;
	onRetry: () => void;
};

/**
 * Which Detailed printout the panel below is editing: Printout 1 plus up to two saved printouts, picked
 * from a short radio list (`PrintoutPresetPicker`) whose chosen row carries the brand wash. A chosen saved
 * printout is renamed and removed just under the list; Add sits at the foot and copies the chosen printout.
 *
 * A list rather than chips or a dropdown because names are long: in a ~280px column every row gets the
 * full width for its name.
 *
 * Collapsed by default, to one header line naming the chosen printout, so the settings it changes start
 * higher in the column; expanded it shows the list. Back to collapsed whenever the bar mounts again
 * (reopening the popup or coming back to the Detailed tab).
 */
export const PrintoutPresetBar: FC<Props> = ({
	status,
	presets,
	activePresetId,
	isAtLimit,
	isAdding,
	isRetrying,
	locked,
	onSelect,
	onAdd,
	onRename,
	onRemove,
	onRetry,
}) => {
	const { t } = useTranslation();
	const labelId = useId();
	const printout1Label = t("printout.preset.label.first");
	const activePreset = presets.find((preset) => preset.id === activePresetId);
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	// Kept after the dialog closes, so its title doesn't lose the name during the exit transition.
	const [removeTarget, setRemoveTarget] = useState<PrintoutPreset | null>(null);
	const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState<boolean>(false);
	/**
	 * From the confirm click until the removal settles — which includes waiting for a save already on its
	 * way, before the delete is even sent. A ref as well as state, so a second click in the same frame
	 * (before the re-render disables the button) can't send a second delete either.
	 */
	const [isRemoving, setIsRemoving] = useState<boolean>(false);
	const removingRef = useRef<boolean>(false);

	const requestRemove = (preset: PrintoutPreset): void => {
		setRemoveTarget(preset);
		setIsRemoveDialogOpen(true);
	};

	const confirmRemove = async (presetId: string): Promise<void> => {
		if (removingRef.current) return;
		removingRef.current = true;
		setIsRemoving(true);
		try {
			await onRemove(presetId);
		} finally {
			removingRef.current = false;
			setIsRemoving(false);
			// Closed either way: a failure is already on screen as a snackbar, and the printout is still there to retry from.
			setIsRemoveDialogOpen(false);
		}
	};

	return (
		<Box
			// Not labelled itself: the picker inside is labelled by the same heading, and one name is enough.
			data-testid="printout-preset-bar"
			sx={PRINTOUT_LIST_CARD_SX}
		>
			<CollapsibleSection
				variant={CollapsibleSectionVariant.Eyebrow}
				title={t("printout.preset.list.label")}
				summary={activePreset ? activePreset.name : printout1Label}
				titleId={labelId}
				open={isExpanded}
				onToggle={() => setIsExpanded((expanded) => !expanded)}
				dataTestId="printout-preset-bar-toggle"
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 0.5 }}>
					<PrintoutPresetPicker
						labelId={labelId}
						printout1Label={printout1Label}
						presets={presets}
						activePresetId={activePresetId}
						loading={status === "loading"}
						disabled={locked}
						onSelect={onSelect}
					/>

					{activePreset && (
						<PrintoutPresetEditor
							key={activePreset.id}
							presetId={activePreset.id}
							name={activePreset.name}
							otherNames={presets.filter((other) => other.id !== activePreset.id).map((other) => other.name)}
							printout1Label={printout1Label}
							onRename={(name) => onRename(activePreset.id, name)}
							onRemoveRequest={() => requestRemove(activePreset)}
						/>
					)}

					{status === "error" && (
						<InlineHint
							tone="warning"
							dataTestId="printout-preset-load-error"
							actionPlacement="below"
							action={
								<SystemButton data-testid="printout-preset-load-retry" buttonVariant="borderless" size="small" onClick={onRetry} loading={isRetrying}>
									{t("retry")}
								</SystemButton>
							}
						>
							{t("printout.preset.load.failed")}
						</InlineHint>
					)}

					{status === "ready" && (
						// The "Detailed only" note sits beside Add rather than in the header: the header is the
						// toggle button, and the note's own button can't be nested inside it.
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<InHouseTooltip
								title={isAtLimit ? t("printout.preset.add.limit.tooltip", { max: MAX_PRINTOUT_PRESETS }) : t("printout.preset.add.tooltip")}
								placement="bottom-start"
							>
								{/* A wrapper the tooltip can hold a ref to — and that still hears the hover when the button is disabled. */}
								<Box component="span" sx={{ display: "inline-flex" }}>
									<DashedAddButton
										label={t("printout.preset.add")}
										onClick={onAdd}
										disabled={isAtLimit || isAdding || locked}
										dataTestId="printout-preset-add"
									/>
								</Box>
							</InHouseTooltip>
							<InfoIconWithTooltip
								title={t("printout.preset.detailed.only.hint")}
								dataTestId="printout-preset-detailed-only-info"
								iconSx={{ fontSize: ICON_SIZE.MICRO, color: INK.MUTED }}
							/>
						</Box>
					)}

					<InlineHint open={locked} tone="neutral" dataTestId="printout-preset-locked-ai">
						{t("printout.preset.locked.ai")}
					</InlineHint>
				</Box>
			</CollapsibleSection>

			{removeTarget && (
				<SystemDialog
					open={isRemoveDialogOpen}
					onClose={() => setIsRemoveDialogOpen(false)}
					title={t("printout.preset.remove.confirm.title", { name: removeTarget.name })}
					body={t("printout.preset.remove.confirm.body", { name: removeTarget.name })}
					confirm={t("printout.preset.remove.confirm.confirm")}
					cancel={t("printout.preset.remove.confirm.cancel")}
					confirmButtonVariant="delete"
					onConfirm={() => void confirmRemove(removeTarget.id)}
					confirmLoading={isRemoving}
					confirmDisabled={locked || isRemoving}
					confirmTooltip={locked ? t("printout.preset.locked.ai") : undefined}
					dataTestId="printout-preset-remove-dialog"
					confirmDataTestId="printout-preset-remove-confirm"
					cancelDataTestId="printout-preset-remove-cancel"
				/>
			)}
		</Box>
	);
};
